// Saving the site as an app (a "Progressive Web App": an icon on the home screen that opens the site
// in its own window, from public/manifest.json). Chrome, Edge and Samsung Internet offer that through
// the `beforeinstallprompt` event, which fires once, early, and is kept here until a button asks for
// it - so this module is imported by index.js before React renders. Safari on iPhone and iPad has no
// such event: there the visitor adds the site themself (Share, then "Add to Home Screen"), and the
// prompt only explains how. Firefox and desktop Safari offer neither, so no button is shown there.
// The offer is for phones and tablets only (customer, 2026-11-03): on a computer nothing is shown,
// and the browser keeps its own install icon in the address bar.

const DISMISSED_KEY = 'installAppDismissed';

let deferredPrompt = null;
let installed = false;
let helpOpen = false;
const listeners = new Set();

const notify = () => listeners.forEach((listener) => listener());

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    if (!isMobileDevice()) return;
    // Keep the browser's own mini-infobar away; the site's prompt offers the install instead.
    event.preventDefault();
    deferredPrompt = event;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    installed = true;
    helpOpen = false;
    notify();
  });
}

// Already opened from the home screen (the app window): nothing to offer.
export const isStandalone = () => {
  try {
    return window.navigator.standalone === true
      || (typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches);
  } catch (e) {
    return false;
  }
};

// iPhone, iPod and iPad (an iPad reports itself as a Mac with a touch screen).
export const isIos = () => {
  const { userAgent = '', platform = '', maxTouchPoints = 0 } = window.navigator;
  return /iPad|iPhone|iPod/.test(userAgent) || (platform === 'MacIntel' && maxTouchPoints > 1);
};

// Phones and tablets: Android, iPhone, iPod, iPad.
export const isMobileDevice = () => /Android|iPhone|iPad|iPod/.test(window.navigator.userAgent || '') || isIos();

// What the visitor can do here: 'prompt' (the browser's install dialog), 'ios' (the steps by hand)
// or null (nothing: a computer, already installed, or a browser without either).
export const installMode = () => {
  if (installed || isStandalone() || !isMobileDevice()) return null;
  if (deferredPrompt) return 'prompt';
  if (isIos()) return 'ios';
  return null;
};

export const getInstallState = () => ({ mode: installMode(), helpOpen });

export const subscribeInstall = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

// Opens the browser's dialog where there is one, otherwise the steps for iPhone and iPad.
export const requestInstall = async () => {
  if (deferredPrompt) {
    const event = deferredPrompt;
    // The event can be used once; Chrome fires a new one if the visitor declines.
    deferredPrompt = null;
    notify();
    try {
      await event.prompt();
      await event.userChoice;
    } catch (e) {
      // A prompt the browser refused leaves nothing to clean up.
    }
    return;
  }
  if (isIos()) {
    helpOpen = true;
    notify();
  }
};

export const closeInstallHelp = () => {
  helpOpen = false;
  notify();
};

export const isInstallDismissed = () => {
  try {
    return localStorage.getItem(DISMISSED_KEY) === '1';
  } catch (e) {
    return false;
  }
};

export const dismissInstall = () => {
  try {
    localStorage.setItem(DISMISSED_KEY, '1');
  } catch (e) {
    // Without storage the prompt simply comes back on the next visit.
  }
  helpOpen = false;
  notify();
};

// Test hook: forget the kept event and the flags.
export const resetInstallStateForTest = () => {
  deferredPrompt = null;
  installed = false;
  helpOpen = false;
};
